import { LitElement, css, html, nothing, type PropertyValues } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { styleMap } from "lit/directives/style-map.js";
import "./a2k-icon/a2k-icon";

export interface A2kFileTreeEntry {
  path: string;
  size: number;
  editable: boolean;
  sha256: string | null;
}

interface TreeNode {
  id: string;
  name: string;
  path: string;
  depth: number;
  kind: "directory" | "file";
  parentId: string | null;
  children: TreeNode[];
  file?: A2kFileTreeEntry;
}

const byName = (left: TreeNode, right: TreeNode) => {
  if (left.kind !== right.kind) return left.kind === "directory" ? -1 : 1;
  return left.name.localeCompare(right.name, undefined, {
    numeric: true,
    sensitivity: "base",
  });
};

function safePath(path: string) {
  const value = path.trim();
  if (
    !value ||
    value.startsWith("/") ||
    value.startsWith("\\") ||
    /^[a-zA-Z]:[\\/]/.test(value)
  ) {
    return null;
  }
  const parts = value.replaceAll("\\", "/").split("/");
  if (parts.some((part) => part === "..")) return null;
  const clean = parts.filter((part) => part && part !== ".");
  return clean.length ? clean.join("/") : null;
}

function buildTree(files: A2kFileTreeEntry[]) {
  const roots: TreeNode[] = [];
  const nodes = new Map<string, TreeNode>();

  for (const file of files) {
    const path = safePath(file.path);
    if (!path) continue;
    const parts = path.split("/");
    let parent: TreeNode | null = null;
    let currentPath = "";

    parts.forEach((name, index) => {
      currentPath = currentPath ? `${currentPath}/${name}` : name;
      const isFile = index === parts.length - 1;
      let node = nodes.get(currentPath);
      if (!node) {
        node = {
          id: `${isFile ? "file" : "directory"}:${currentPath}`,
          name,
          path: currentPath,
          depth: index + 1,
          kind: isFile ? "file" : "directory",
          parentId: parent?.id ?? null,
          children: [],
          file: isFile ? { ...file, path } : undefined,
        };
        nodes.set(currentPath, node);
        (parent ? parent.children : roots).push(node);
      } else if (isFile && node.kind === "file") {
        node.file = { ...file, path };
      }
      parent = node;
    });
  }

  const sort = (items: TreeNode[]) => {
    items.sort(byName);
    items.forEach((item) => sort(item.children));
  };
  sort(roots);
  return roots;
}

@customElement("a2k-file-tree")
export class A2kFileTree extends LitElement {
  @property({ attribute: false }) files: A2kFileTreeEntry[] = [];
  @property() height = "18rem";
  @property({ attribute: "max-height" }) maxHeight = "50vh";
  @property({ attribute: "aria-label" }) ariaLabel = "Files";
  @state() private expanded = new Set<string>();
  @state() private selectedId = "";
  private roots: TreeNode[] = [];

  static styles = css`
    :host {
      display: block;
      min-width: 0;
      color: #000;
      font-family: "MS Sans Serif", Tahoma, Arial, sans-serif;
      font-size: 12px;
    }
    .tree {
      box-sizing: border-box;
      width: 100%;
      min-width: 0;
      overflow: auto;
      overscroll-behavior: contain;
      scrollbar-gutter: stable;
      padding: 3px;
      border-top: 2px solid #404040;
      border-left: 2px solid #404040;
      border-right: 2px solid #fff;
      border-bottom: 2px solid #fff;
      background: #fff;
      outline: 1px solid #808080;
      outline-offset: -2px;
    }
    .tree:focus-visible {
      outline: 2px solid #000;
      outline-offset: 1px;
    }
    .item {
      display: flex;
      align-items: center;
      width: max-content;
      min-width: 100%;
      height: 24px;
      box-sizing: border-box;
      padding: 1px 5px 1px calc(4px + (var(--depth) - 1) * 18px);
      gap: 4px;
      white-space: nowrap;
      cursor: default;
      user-select: none;
    }
    .item[aria-selected="true"] {
      color: #fff;
      background: #000080;
    }
    .item:focus-visible {
      outline: 1px dotted currentColor;
      outline-offset: -2px;
    }
    .twisty {
      display: inline-flex;
      flex: 0 0 13px;
      align-items: center;
      justify-content: center;
      width: 13px;
      height: 13px;
      box-sizing: border-box;
      padding: 0;
      border: 1px solid #808080;
      color: #000;
      background: #c0c0c0;
      font: bold 12px/10px monospace;
    }
    .item[aria-selected="true"] .twisty {
      border-color: #fff;
    }
    .twisty-spacer {
      flex: 0 0 13px;
      width: 13px;
    }
    .icon {
      display: inline-flex;
      flex: 0 0 16px;
      width: 16px;
      height: 16px;
      align-items: center;
      justify-content: center;
    }
    .icon a2k-icon {
      font-size: 16px;
    }
    .icon svg {
      display: block;
      width: 16px;
      height: 16px;
      fill: #fff;
      stroke-width: 1.6;
    }
    .directory .icon svg {
      fill: #ffe072;
      color: #5c4800;
    }
    .editable .icon svg {
      fill: #e8f2ff;
      color: #123d72;
    }
    .label {
      display: block;
      min-width: 0;
      max-width: min(38rem, calc(100vw - 100px));
      overflow: hidden;
      text-overflow: ellipsis;
    }
    @media (max-width: 480px) {
      .tree {
        scrollbar-gutter: auto;
      }
      .label {
        max-width: calc(100vw - 116px);
      }
    }
  `;

  protected willUpdate(changed: PropertyValues<this>) {
    if (!changed.has("files")) return;
    this.roots = buildTree(this.files);
    const directoryIds = this.allNodes(this.roots)
      .filter((node) => node.kind === "directory")
      .map((node) => node.id);
    const knownIds = new Set(this.allNodes(this.roots).map((node) => node.id));
    const retained = [...this.expanded].filter((id) => knownIds.has(id));
    this.expanded = new Set(
      changed.get("files") === undefined ? directoryIds : retained,
    );
    if (!knownIds.has(this.selectedId)) this.selectedId = "";
  }

  private allNodes(nodes: TreeNode[]): TreeNode[] {
    return nodes.flatMap((node) => [node, ...this.allNodes(node.children)]);
  }

  private visibleNodes(nodes = this.roots): TreeNode[] {
    return nodes.flatMap((node) => [
      node,
      ...(node.kind === "directory" && this.expanded.has(node.id)
        ? this.visibleNodes(node.children)
        : []),
    ]);
  }

  private toggle(node: TreeNode) {
    if (node.kind !== "directory") return;
    const expanded = new Set(this.expanded);
    if (expanded.has(node.id)) expanded.delete(node.id);
    else expanded.add(node.id);
    this.expanded = expanded;
  }

  private select(node: TreeNode, activate = false) {
    this.selectedId = node.id;
    if (activate && node.file) {
      this.dispatchEvent(
        new CustomEvent<A2kFileTreeEntry>("file-select", {
          detail: node.file,
          bubbles: true,
          composed: true,
        }),
      );
    }
  }

  private focusNode(node: TreeNode) {
    this.select(node);
    void this.updateComplete.then(() => {
      const items = this.renderRoot.querySelectorAll<HTMLElement>(".item");
      [...items].find((item) => item.dataset.nodeId === node.id)?.focus();
    });
  }

  private onClick(node: TreeNode) {
    this.select(node, node.kind === "file");
    if (node.kind === "directory") this.toggle(node);
  }

  private onKeyDown(event: KeyboardEvent, node: TreeNode) {
    const visible = this.visibleNodes();
    const index = visible.findIndex((item) => item.id === node.id);
    let target: TreeNode | undefined;

    switch (event.key) {
      case "ArrowDown":
        target = visible[Math.min(index + 1, visible.length - 1)];
        break;
      case "ArrowUp":
        target = visible[Math.max(index - 1, 0)];
        break;
      case "Home":
        target = visible[0];
        break;
      case "End":
        target = visible.at(-1);
        break;
      case "ArrowRight":
        if (node.kind === "directory" && !this.expanded.has(node.id)) {
          this.toggle(node);
        } else if (node.kind === "directory") {
          target = node.children[0];
        }
        break;
      case "ArrowLeft":
        if (node.kind === "directory" && this.expanded.has(node.id)) {
          this.toggle(node);
        } else if (node.parentId) {
          target = this.allNodes(this.roots).find(
            (item) => item.id === node.parentId,
          );
        }
        break;
      case "Enter":
      case " ":
        if (node.kind === "directory") this.toggle(node);
        else this.select(node, true);
        break;
      default:
        return;
    }
    event.preventDefault();
    event.stopPropagation();
    if (target) this.focusNode(target);
  }

  private renderNode(node: TreeNode, focusableId: string) {
    const isDirectory = node.kind === "directory";
    const isExpanded = isDirectory && this.expanded.has(node.id);
    const icon = isDirectory
      ? isExpanded
        ? "documents98-icon"
        : "documents98-icon"
      : node.file?.editable
        ? "txt98-icon"
        : "documents98-icon";
    return html`
      <div
        class="item ${node.kind} ${node.file?.editable ? "editable" : ""}"
        role="treeitem"
        aria-level=${node.depth}
        aria-selected=${this.selectedId === node.id ? "true" : "false"}
        aria-expanded=${isDirectory ? String(isExpanded) : nothing}
        tabindex=${focusableId === node.id ? "0" : "-1"}
        data-node-id=${node.id}
        style=${styleMap({ "--depth": String(node.depth) })}
        title=${node.path}
        @click=${() => this.onClick(node)}
        @keydown=${(event: KeyboardEvent) => this.onKeyDown(event, node)}
      >
        ${
          isDirectory
            ? html`<span class="twisty" aria-hidden="true"
                >${isExpanded ? "-" : "+"}</span
              >`
            : html`<span class="twisty-spacer" aria-hidden="true"></span>`
        }
        <span class="icon" aria-hidden="true"
          ><a2k-icon icon=${icon}></a2k-icon
        ></span>
        <span class="label">${node.name}</span>
      </div>
    `;
  }

  render() {
    const visible = this.visibleNodes();
    const focusableId = this.selectedId || visible[0]?.id || "";
    const styles = {
      height: this.height || undefined,
      "max-height": this.maxHeight || undefined,
    };
    return html`
      <div
        class="tree"
        role="tree"
        aria-label=${this.ariaLabel}
        style=${styleMap(styles)}
        tabindex=${visible.length ? "-1" : "0"}
      >
        ${visible.map((node) => this.renderNode(node, focusableId))}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "a2k-file-tree": A2kFileTree;
  }
}
